import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { CardView } from "../api/types";
import type { Theme } from "../theme/pixel";
import { Card, naturalCardWidthPx } from "./Card";
import { PickOrderBadge } from "./PickOrderBadge";
import { E32 } from "../theme/endesga32";
import { cardKey, useBoardRefs } from "../logic/boardRefs";
import {
  effectiveLayoutId,
  patchCardView,
  useCardOverrides,
} from "../logic/cardOverrides";
import { zoneMoveTransition } from "../logic/motion";

// Shared last-known cursor position used by every slot's geometric
// hover poll. Module-level so the per-render check has something to
// compare against even when this particular slot's pointermove
// listener hasn't fired yet (a freshly-mounted slot, a re-render
// triggered by a hand reflow, …). Sentinel `-1` = "no pointer seen
// yet"; the per-render check no-ops in that case.
let lastPointerXShared = -1;
let lastPointerYShared = -1;
if (typeof window !== "undefined") {
  window.addEventListener(
    "pointermove",
    (e) => {
      lastPointerXShared = e.clientX;
      lastPointerYShared = e.clientY;
    },
    { capture: true, passive: true },
  );
}

interface Props {
  hand: CardView[];
  mana: number;
  theme: Theme;
  /** Hand index currently selected (inspected), or null. */
  selectedIdx: number | null;
  /** False outside the main phase. */
  canPlay: boolean;
  onSelect: (idx: number | null, card: CardView | null) => void;
  /** Wired only when in main phase and the card is affordable. Drop on
   *  any element tagged `data-battle-drop-zone` plays the card. */
  /** Called when the user drops a dragged card on the battlefield.
   *  `releaseRect` is where the dragged clone landed in viewport
   *  coordinates — BattlePage hands it to `submitAction` so the play
   *  hover renders from the actual release point instead of from the
   *  hand slot the card came out of. */
  onPlayByDrag?: (handIdx: number, releaseRect: DOMRect) => void;
  /** Keyboard-only play path. Activating an already-selected playable
   *  card (Enter / Space) fires this — the parent submits the play
   *  action with a synthesised release rect centred on the slot. */
  onPlayByKeyboard?: (handIdx: number, slotRect: DOMRect) => void;
  /** Entity id of the card currently in the play-hover phase, if any.
   *  Every OTHER slot dims and refuses drag/click so the user can't
   *  start a second cast on top of the in-flight one — which would
   *  fire its play SFX, only to have the engine reject the second
   *  card and snap it back to hand. */
  castingEid?: number | null;
  /** Eids whose slot must render invisibly while preserving layout —
   *  used by the spell-with-target flow: the engine has the spell
   *  back in hand after the play action, but the user has already
   *  seen the clone fizzle, so surfacing the card again in the hand
   *  would look like a glitch. The slot still occupies its width so
   *  the rest of the hand doesn't suddenly re-centre. */
  hiddenEids?: ReadonlyArray<number>;
  /** Eids whose play action would be rejected by the engine because
   *  no legal targets exist (e.g. a "kill target creature" spell when
   *  the board is empty). Cards in this set render as locked so the
   *  user doesn't waste a click and then watch the card snap back to
   *  hand. The engine remains authoritative — this is a UX hint only. */
  noTargetEids?: ReadonlyArray<number>;
  /** Mana cost of any cast that's been committed locally but whose
   *  engine `mana` scalar event hasn't applied yet. Subtracted from
   *  `mana` for the affordability check so other hand cards grey out
   *  the INSTANT the player commits a play, and STAY grey through
   *  the full hover → network → apply window without the
   *  intermediate flicker that came from looking the cost up via
   *  `castingEid` (which clears as soon as the entity moves out of
   *  hand, before the trailing `mana` event lands). 0 when no cast is
   *  in flight. */
  pendingManaCost?: number;
  /** Active only while a target prompt for the viewer is open (and not
   *  mid-animation). Some spells target a card in your OWN hand (e.g.
   *  "exile a creature from your hand"); the candidates then live in
   *  this row, not on the battlefield, so the hand has to become the
   *  picker. When true, a candidate slot lights amber and a click picks
   *  it (via `onPickTarget`) instead of inspecting; non-candidates dim. */
  targetPicking?: boolean;
  /** Eids in this hand that are legal targets for the open prompt. */
  targetCandidateEids?: ReadonlySet<number>;
  /** Picked target eid → 1-based pick order, for the corner badge. */
  targetPickOrder?: ReadonlyMap<number, number>;
  /** Toggle a hand card in/out of the current target picks. */
  onPickTarget?: (eid: number) => void;
}

export function HandRow({
  hand,
  mana,
  theme,
  selectedIdx,
  canPlay,
  onSelect,
  onPlayByDrag,
  onPlayByKeyboard,
  castingEid,
  hiddenEids,
  noTargetEids,
  pendingManaCost,
  targetPicking,
  targetCandidateEids,
  targetPickOrder,
  onPickTarget,
}: Props) {
  const count = hand.length;
  const totalPicks = targetPickOrder?.size ?? 0;
  // Optimistic deduction: subtract the committed-but-not-yet-applied
  // cast cost from `mana` so the affordability check matches what the
  // engine will report once events apply. The caller (BattlePage)
  // owns the lifetime of `pendingManaCost` and clears it when the
  // engine's `mana` scalar actually lands. A per-frame lookup
  // through `castingEid → hand → cost` used to do this, but the
  // entity moves out of hand mid-batch (the Move event lands before
  // the trailing `mana` scalar in the reducer's apply loop), which
  // dropped the cost to 0 and let the hand cards briefly read as
  // playable again — the "grey → ungrey → grey" flicker.
  const effectiveMana = mana - (pendingManaCost ?? 0);
  const containerStyle: CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: 8,
    padding: "0 20px 8px",
    background: "transparent",
    // Slot wrapper is shorter than the card so cards extend up into
    // the field above while the row stays compact.
    height: 120,
    overflow: "visible",
  };

  return (
    <div style={containerStyle}>
      {/* popLayout: a hand card that leaves to grave / exile / opp
          hand stays painted while Motion FLIPs it to the destination
          pile ghost, while the surviving hand cards re-centre at the
          new layout immediately. Without this, a discarded card would
          just blink out of the hand. */}
      <AnimatePresence mode="popLayout" initial={false}>
      {hand.map((card, i) => {
        const isCastingThis = castingEid === card.entity_id;
        const explicitlyHidden = hiddenEids?.includes(card.entity_id) ?? false;
        // Cast-in-flight card hides its hand slot — the clone is doing
        // the visible work and a clickable copy would let the user
        // drag it again. visibility:hidden preserves slot width so
        // surrounding cards don't re-centre mid-cast.
        const isHidden = explicitlyHidden || isCastingThis;
        const locked = castingEid != null && !isCastingThis;
        const noTargets = noTargetEids?.includes(card.entity_id) ?? false;
        // The v2 wire reserves `EntityRecord.castable` and a
        // `Castable` event for engine-authoritative playability, but
        // the translator doesn't compute either yet — it hardcodes
        // `false`. Until that lands, decide locally from phase + mana
        // + a "would the engine even let this spell pick a target?"
        // heuristic (no creatures on the board → no targets for a
        // `target creature` spell). The engine still rejects illegal
        // plays as the authoritative gate.
        const playable =
          canPlay && card.cost <= effectiveMana && !locked && !isHidden && !noTargets;
        const isSelected = selectedIdx === i;
        const isTargetCandidate =
          (targetPicking && targetCandidateEids?.has(card.entity_id)) ?? false;
        const pickOrder = targetPicking
          ? targetPickOrder?.get(card.entity_id)
          : undefined;
        const showPickOrder = pickOrder !== undefined && totalPicks > 1;
        const center = (count - 1) / 2;
        const offset = i - center;
        const rotate = offset * 2.2;
        const translateY = Math.abs(offset) * 2;
        return (
          // Key by entity_id, not by index: when a card leaves the
          // hand mid-animation (cast, discard, bounce, …) the
          // surviving slots' indices shift and an index-keyed list
          // would remount every slot after the gap, dropping any
          // local state — most visibly the slot's `hovered`
          // boolean. The user would see the hand card they were
          // hovering "snap back down" mid-animation and then pop
          // back up the next time the cursor moved. Keying by eid
          // keeps each slot's instance stable across the move, so the
          // hover state survives unchanged.
          <HandSlot
            key={card.entity_id}
            card={card}
            slotIndex={i}
            theme={theme}
            playable={playable}
            isSelected={isSelected}
            rotate={rotate}
            translateY={translateY}
            locked={locked || isHidden}
            hidden={isHidden}
            targetPicking={targetPicking ?? false}
            isTargetCandidate={isTargetCandidate}
            pickOrder={pickOrder}
            showPickOrder={showPickOrder}
            onClick={
              locked || isHidden
                ? () => {}
                : targetPicking && isTargetCandidate
                  ? () => onPickTarget?.(card.entity_id)
                  : () => onSelect(isSelected ? null : i, isSelected ? null : card)
            }
            onPlayByDrag={
              playable && onPlayByDrag
                ? (releaseRect) => onPlayByDrag(i, releaseRect)
                : undefined
            }
            onPlayByKeyboard={
              playable && onPlayByKeyboard
                ? (rect) => onPlayByKeyboard(i, rect)
                : undefined
            }
          />
        );
      })}
      </AnimatePresence>
    </div>
  );
}

function HandSlot({
  card,
  slotIndex,
  theme,
  playable,
  isSelected,
  rotate,
  translateY,
  locked,
  hidden,
  targetPicking,
  isTargetCandidate,
  pickOrder,
  showPickOrder,
  onClick,
  onPlayByDrag,
  onPlayByKeyboard,
}: {
  card: CardView;
  slotIndex: number;
  theme: Theme;
  playable: boolean;
  isSelected: boolean;
  rotate: number;
  translateY: number;
  /** Another card is currently mid-cast; this slot is rendered dim
   *  and refuses both click and drag. */
  locked: boolean;
  /** Stronger than `locked` — the slot is rendered fully invisible
   *  while preserving its width, so the surrounding hand doesn't
   *  shift. Used during the spell-with-target picker phase. */
  hidden: boolean;
  /** A target prompt is open and this row is the picker. */
  targetPicking: boolean;
  /** This card is a legal target for the open prompt. */
  isTargetCandidate: boolean;
  /** 1-based pick order if this card is currently picked. */
  pickOrder?: number;
  /** Whether to render the numeric pick-order badge (suppressed for
   *  single-target prompts where the order carries no signal). */
  showPickOrder: boolean;
  onClick: () => void;
  onPlayByDrag?: (releaseRect: DOMRect) => void;
  onPlayByKeyboard?: (rect: DOMRect) => void;
}) {
  const [hovered, setHovered] = useState(false);
  // Ref drives the synchronous mousemove read; state drives re-render
  // so the floating card follows the cursor.
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);
  const liveRef = useRef({ onPlayByDrag });
  liveRef.current = { onPlayByDrag };
  const boardRefs = useBoardRefs();
  const cardOverrides = useCardOverrides();
  const renderCard = patchCardView(card, cardOverrides);
  // Geometric hover tracker. React's onMouseEnter/Leave is implemented
  // via mouseover/mouseout + relatedTarget filtering on the slot's
  // subtree. A geometric "is the cursor inside this rect right now"
  // poll answers the user-meaningful question rather than tracking
  // descendant-element churn through hand reflows.
  //
  // Drive the poll from BOTH window pointermove (catches active cursor
  // motion) AND a per-render check via useLayoutEffect (catches the
  // case where the slot's DOM moves sideways under a stationary
  // cursor when the hand reflows after a card leaves). The cursor's
  // last screen position is shared across slots via a module-level
  // mutable cell so the per-render check has something to compare
  // against even when no event has fired yet this frame.
  const slotRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (hidden) {
      // Slot is invisible (its card is mid-cast, or it's
      // explicitly hidden for the spell-with-target picker).
      // Suppress hover so the lifted state doesn't outlive the
      // slot's visibility.
      if (hovered) setHovered(false);
      return;
    }
    const onMove = (e: PointerEvent) => {
      lastPointerXShared = e.clientX;
      lastPointerYShared = e.clientY;
      const el = slotRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return;
      const inside =
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom;
      setHovered((cur) => (cur === inside ? cur : inside));
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [hidden, hovered]);
  // Per-render geometric check: when the hand reflows (a card
  // played / discarded / bounced), slots shift sideways under a
  // stationary cursor. pointermove won't fire from that — the
  // mouse hasn't moved — but the slot's new bounding rect may now
  // be on the wrong side of the cursor. Re-poll on every render
  // against the last-known cursor position so hover state tracks
  // the user's visual experience, not the slot's flex
  // reflow accounting.
  useLayoutEffect(() => {
    if (hidden) return;
    if (lastPointerXShared < 0 || lastPointerYShared < 0) return;
    const el = slotRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;
    const inside =
      lastPointerXShared >= r.left &&
      lastPointerXShared <= r.right &&
      lastPointerYShared >= r.top &&
      lastPointerYShared <= r.bottom;
    setHovered((cur) => (cur === inside ? cur : inside));
  });
  // The slot's motion.div always carries the layoutId. While dragging,
  // the slot's positioning wrapper is hidden via visibility:hidden so
  // the user only sees the drag clone, but the layoutId stays put so
  // Motion can FLIP from the hand slot to whatever destination the
  // card lands at after the drop (the play-hover clone takes over the
  // visual transition from the release rect on a successful play).
  const slotLayoutId = effectiveLayoutId(card.entity_id, cardOverrides);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (drag === null) {
        if (dx * dx + dy * dy < 36) return;
      }
      setDrag({ x: e.clientX, y: e.clientY });
    };
    const up = (e: MouseEvent) => {
      const wasDragging = drag !== null;
      dragStartRef.current = null;
      if (!wasDragging) return;
      setDrag(null);
      suppressClickRef.current = true;
      const target = document.elementFromPoint(e.clientX, e.clientY);
      const onDropZone = target?.closest("[data-battle-drop-zone]");
      if (onDropZone) {
        // Rect centred on release, matching the dragged clone's
        // natural size (5/7 aspect, normal width).
        const w = naturalCardWidthPx("normal");
        const h = (w * 7) / 5;
        const rect = new DOMRect(e.clientX - w / 2, e.clientY - h / 2, w, h);
        liveRef.current.onPlayByDrag?.(rect);
      }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [drag]);

  const baseScale = 0.72;
  const scale = hovered && !drag ? 1.0 : isSelected ? 0.78 : baseScale;
  const lift = hovered && !drag ? -70 : isSelected ? -18 : translateY;
  const rot = hovered && !drag ? 0 : rotate;
  const cardW = naturalCardWidthPx("normal");
  const footW = cardW * baseScale;

  const normalTransform = `translateX(-50%) translateY(${lift}px) rotate(${rot}deg) scale(${scale})`;
  // Slot bottom-anchors the Card and is sized to cover the Card's
  // at-rest visual extent (scaled 5/7 aspect) so hover/click hit-tests
  // always land on the slot regardless of Card's inner transforms.
  const cardVisHeight = Math.ceil((cardW * 7) / 5 * baseScale);
  const slotHeight = cardVisHeight;

  const interactive = !hidden && !locked;
  const isTargetPicked = pickOrder !== undefined;
  // Amber halo = legal target; scarlet = currently picked. Mirrors the
  // battlefield picker's rings (see Field.tsx) so the two read the same.
  const targetRingColor = isTargetPicked
    ? E32.scarlet
    : isTargetCandidate
      ? `${E32.amber}80`
      : undefined;
  // During target picking the affordability dim is meaningless (it's not
  // the main phase); dim non-candidates instead so the pickable card
  // pops out of the row.
  const dimmed = targetPicking ? !isTargetCandidate : !playable;
  const slotAriaLabel = `${card.name} in hand slot ${slotIndex + 1}${
    targetPicking
      ? isTargetCandidate
        ? ", targetable"
        : ", not targetable"
      : playable
        ? ""
        : ", unplayable"
  }${isSelected ? ", selected" : ""}`;
  return (
    // motion.div (rather than plain div) so the wrapping
    // <AnimatePresence> in HandRow can manage this slot's exit
    // lifecycle — without it the slot unmounts the moment a card
    // leaves the hand and Motion's shared layoutId transition has
    // nothing to animate from. No animate / layout / layoutId props
    // here; the inner positioning wrapper holds the card's layoutId.
    <motion.div
      ref={(el) => {
        slotRef.current = el;
        boardRefs.register(cardKey(card.entity_id), el);
      }}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : -1}
      aria-label={interactive ? slotAriaLabel : undefined}
      aria-pressed={interactive ? isSelected : undefined}
      style={{
        position: "relative",
        width: footW,
        height: slotHeight,
        margin: "0 -10px",
        zIndex: drag ? 200 : hovered ? 30 : isSelected ? 5 : 1,
        userSelect: "none",
        WebkitUserSelect: "none",
        // Use visibility:hidden (not display:none) so the slot keeps
        // its width and the hand stays centred.
        visibility: hidden ? "hidden" : undefined,
        pointerEvents: hidden ? "none" : undefined,
      }}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={() => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        onClick();
      }}
      onKeyDown={(e) => {
        if (!interactive) return;
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        // Keyboard semantics: first activation selects the slot
        // (mirrors a click). A second activation on an already-selected
        // playable card plays it via the keyboard-play path, with a
        // release rect centred on the slot so the play-clone animation
        // launches from the slot the user is looking at.
        if (isSelected && playable && onPlayByKeyboard) {
          const el = slotRef.current;
          if (el) {
            const r = el.getBoundingClientRect();
            const rect = new DOMRect(
              r.left + r.width / 2 - naturalCardWidthPx("normal") / 2,
              r.top + r.height / 2 - (naturalCardWidthPx("normal") * 7) / 10,
              naturalCardWidthPx("normal"),
              (naturalCardWidthPx("normal") * 7) / 5,
            );
            onPlayByKeyboard(rect);
            return;
          }
        }
        onClick();
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        // Refuse drag-to-play while another card is mid-cast.
        if (locked) return;
        if (onPlayByDrag) {
          dragStartRef.current = { x: e.clientX, y: e.clientY };
        }
      }}
    >
      {drag ? (
        // Plain div, not a motion.div: the drag clone is positioned
        // manually under the cursor on every mousemove, and Motion's
        // layoutId-driven layout animations would either fight the
        // cursor follow (smoothing/lag) or override our centering
        // transform (anchoring the card by its top-left). The slot's
        // motion.div is what holds the layoutId — on a successful
        // play, the play-hover clone (in BattlePage) takes over and
        // animates from the release rect to the field slot via
        // shared layoutId.
        <div
          style={{
            position: "fixed",
            left: drag.x - cardW / 2,
            top: drag.y - ((cardW * 7) / 5) / 2,
            width: cardW,
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          <Card card={renderCard} size="normal" theme={theme} />
        </div>
      ) : null}

      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: "50%",
          width: cardW,
          transform: normalTransform,
          transformOrigin: "bottom center",
          transition: drag ? "none" : "transform 220ms cubic-bezier(.2,.9,.2,1)",
          visibility: drag ? "hidden" : undefined,
        }}
      >
        <motion.div
          layoutId={slotLayoutId}
          transition={zoneMoveTransition()}
          style={{ width: "100%" }}
        >
          <Card
            card={renderCard}
            size="normal"
            theme={theme}
            dim={dimmed}
            selected={isSelected}
            ringColor={targetRingColor}
            ringWidth={isTargetPicked ? 5 : undefined}
          />
        </motion.div>
      </div>
      {showPickOrder && pickOrder !== undefined && (
        <div
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            zIndex: 40,
            pointerEvents: "none",
          }}
        >
          <PickOrderBadge order={pickOrder} theme={theme} />
        </div>
      )}
    </motion.div>
  );
}
